import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import { BlogPost, BlogCategory } from '../../../types/blog';
import AdminBlogTable from './components/AdminBlogTable';
import BlogFormModal from './components/BlogFormModal';
import AdminCategorySection from './components/AdminCategorySection';
import { useAdminAuth } from '../../../hooks/useAdminAuth';
import AdminLoginPage from '../components/AdminLoginPage';

type TabType = 'posts' | 'categories';

export default function AdminBlogPage() {
  const { isAuthenticated, login, logout, error: authError } = useAdminAuth();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPost, setEditPost] = useState<BlogPost | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('posts');

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: cats }, { data: postsData }] = await Promise.all([
      supabase.from('8a4f04ed_blog_categories').select('*').order('created_at'),
      supabase
        .from('8a4f04ed_blog_posts')
        .select('*, category:8a4f04ed_blog_categories(*)')
        .order('published_at', { ascending: false }),
    ]);
    if (cats) setCategories(cats);
    if (postsData) setPosts(postsData as BlogPost[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [fetchData, isAuthenticated]);

  const handleEdit = (post: BlogPost) => {
    setEditPost(post);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditPost(null);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('8a4f04ed_blog_posts').delete().eq('id', id);
    if (error) {
      showToast('error', '削除に失敗しました。');
    } else {
      showToast('success', '記事を削除しました。');
      fetchData();
    }
  };

  const handleSaved = () => {
    showToast('success', editPost ? '記事を更新しました。' : '記事を追加しました。');
    fetchData();
  };

  const handleStatusToggle = async (id: string, current: 'published' | 'draft') => {
    const next = current === 'published' ? 'draft' : 'published';
    const { error } = await supabase
      .from('8a4f04ed_blog_posts')
      .update({ status: next, published: next === 'published', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) {
      showToast('error', 'ステータスの更新に失敗しました。');
    } else {
      showToast('success', next === 'published' ? '記事を公開しました。' : '下書きに変更しました。');
      fetchData();
    }
  };

  if (!isAuthenticated) {
    return <AdminLoginPage onLogin={login} error={authError} />;
  }

  return (
    <div className="min-h-screen" style={{ background: '#fff8f2' }}>
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-6 right-6 z-[100] px-5 py-3 rounded-xl text-sm font-bold"
          style={{
            background: toast.type === 'success' ? '#5a9e6f' : '#cc4444',
            color: '#fff',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Top Bar */}
      <div
        className="sticky top-0 z-40 px-6 h-[64px] flex items-center justify-between"
        style={{ background: '#fff', borderBottom: '2px solid #5a9e6f' }}
      >
        <div className="flex items-center gap-4">
          <Link to="/" style={{ color: '#8b4513', fontWeight: '700', fontSize: '18px' }} className="whitespace-nowrap">
            縁結び 和はは
          </Link>
          <span style={{ color: '#d4c4ba' }}>|</span>
          <h1 style={{ color: '#4a3728', fontWeight: '700', fontSize: '16px' }} className="whitespace-nowrap">
            ブログ管理
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/blog"
            target="_blank"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 whitespace-nowrap"
            style={{ background: '#f2f8f4', color: '#5a9e6f', border: '1px solid #c8e0cf' }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-external-link-line text-sm"></i>
            </span>
            ブログを見る
          </Link>
          {activeTab === 'posts' && (
            <button
              onClick={handleAdd}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: '#5a9e6f' }}
            >
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-add-line text-sm"></i>
              </span>
              新規追加
            </button>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
            style={{ background: '#fdf0ee', color: '#cc4444', border: '1px solid #f0c8c0' }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-logout-box-r-line text-sm"></i>
            </span>
            ログアウト
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>総記事数</p>
            <p className="text-2xl font-bold" style={{ color: '#4a3728' }}>{posts.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>公開中</p>
            <p className="text-2xl font-bold" style={{ color: '#5a9e6f' }}>{posts.filter(p => p.status !== 'draft').length}</p>
          </div>
          <div className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
            <p className="text-xs font-bold mb-1" style={{ color: '#9b8b7f' }}>下書き</p>
            <p className="text-2xl font-bold" style={{ color: '#c4903a' }}>{posts.filter(p => p.status === 'draft').length}</p>
          </div>
          {categories.slice(0, 2).map((cat) => (
            <div key={cat.id} className="bg-white rounded-xl p-4" style={{ border: '1px solid #e8d8cc' }}>
              <p className="text-xs font-bold mb-1 truncate" style={{ color: '#9b8b7f' }}>{cat.name}</p>
              <p className="text-2xl font-bold" style={{ color: cat.color }}>
                {posts.filter((p) => p.category_id === cat.id).length}
              </p>
            </div>
          ))}
        </div>

        {/* Tab Switcher */}
        <div
          className="inline-flex rounded-full p-1 mb-6"
          style={{ background: '#f0e8e0' }}
        >
          <button
            onClick={() => setActiveTab('posts')}
            className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            style={{
              background: activeTab === 'posts' ? '#fff' : 'transparent',
              color: activeTab === 'posts' ? '#4a3728' : '#9b8b7f',
              boxShadow: activeTab === 'posts' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-article-line text-sm"></i>
            </span>
            記事一覧
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className="flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap"
            style={{
              background: activeTab === 'categories' ? '#fff' : 'transparent',
              color: activeTab === 'categories' ? '#4a3728' : '#9b8b7f',
              boxShadow: activeTab === 'categories' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            }}
          >
            <span className="flex items-center justify-center w-4 h-4">
              <i className="ri-folder-line text-sm"></i>
            </span>
            カテゴリー管理
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'posts' ? (
          <div className="bg-white rounded-2xl overflow-hidden" style={{ border: '1px solid #e8d8cc' }}>
            <div
              className="flex items-center justify-between px-6 py-4"
              style={{ borderBottom: '1px solid #e8d8cc' }}
            >
              <h2 className="font-bold" style={{ color: '#4a3728', fontSize: '15px' }}>
                記事一覧
              </h2>
              <span style={{ color: '#9b8b7f', fontSize: '13px' }}>{posts.length}件</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20 gap-3" style={{ color: '#9b8b7f' }}>
                <span className="flex items-center justify-center w-5 h-5">
                  <i className="ri-loader-4-line text-xl animate-spin" style={{ color: '#5a9e6f' }}></i>
                </span>
                読み込み中...
              </div>
            ) : (
              <AdminBlogTable
                posts={posts}
                categories={categories}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onStatusToggle={handleStatusToggle}
              />
            )}
          </div>
        ) : (
          <AdminCategorySection
            categories={categories}
            posts={posts}
            onRefresh={fetchData}
            onToast={showToast}
          />
        )}
      </div>

      {/* Modal */}
      <BlogFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        post={editPost}
        categories={categories}
        onSaved={handleSaved}
      />
    </div>
  );
}
